import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUnreadNotifications, markNotificationAsRead, markAllNotificationsAsRead, getUnreadNotificationCount, deleteNotification } from '../services/notifications';
import './Notifications.css';

export default function Notifications() {
  useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getUnreadNotifications();
      setNotifications(data);
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      await loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      await loadNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleRemoveNotification = async (e, notificationId) => {
    e.stopPropagation(); // Prevent triggering mark as read
    if (window.confirm('Are you sure you want to remove this notification?')) {
      try {
        await deleteNotification(notificationId);
        await loadNotifications();
      } catch (error) {
        console.error('Error removing notification:', error);
        alert('Failed to remove notification: ' + error.message);
      }
    }
  };

  const handleNotificationClick = (e, notification) => {
    e.stopPropagation();
    // Navigate to client's order form tab if client_id exists
    if (notification.client_id) {
      setShowDropdown(false);
      navigate(`/clients/${notification.client_id}`, { 
        state: { activeTab: 'order-form', notificationId: notification.id } 
      });
    } else {
      alert('Client information not available for this notification.');
    }
  };

  return (
    <div className="notifications-container">
      <button
        className="notifications-bell"
        onClick={() => setShowDropdown(!showDropdown)}
        title="Task Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <>
          <div className="notifications-overlay" onClick={() => setShowDropdown(false)} />
          <div className="notifications-dropdown">
            <div className="notifications-header">
              <h3>Task Notifications</h3>
              {unreadCount > 0 && (
                <button
                  className="btn-mark-all-read"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </button>
              )}
            </div>
            <div className="notifications-list">
              {loading ? (
                <div className="notifications-loading">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="notifications-empty">No new notifications</div>
              ) : (
                notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                    onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
                  >
                    <div className="notification-content">
                      <div className="notification-title">
                        Task Completed: {notification.task_title}
                      </div>
                      <div className="notification-meta">
                        <span>Completed by: {notification.completed_by_name}</span>
                        {notification.client_name && (
                          <span> • Client: {notification.client_name}</span>
                        )}
                      </div>
                      <div className="notification-note">
                        {notification.completion_note?.trim() ? notification.completion_note : 'No note provided.'}
                      </div>
                      <div className="notification-time">
                        {new Date(notification.created_at).toLocaleString()}
                      </div>
                      {notification.client_id && (
                        <button
                          className="btn-view-order-form"
                          onClick={(e) => handleNotificationClick(e, notification)}
                          title="View Order Form"
                        >
                          View Order Form
                        </button>
                      )}
                    </div>
                    <div className="notification-actions">
                      {!notification.is_read && (
                        <div className="notification-unread-indicator" />
                      )}
                      <button
                        className="btn-remove-notification"
                        onClick={(e) => handleRemoveNotification(e, notification.id)}
                        title="Remove notification"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
